import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { getIdentity, type Identity, type IdentityWithFallbacks, identitySchema, identityWithFallbacksSchema, postIdentity } from '@/services/api/profile';
import { userAttributesQueryKey } from './use-user-attributes';

const profileIdentityQueryKey = (pid: UserPid) => ['profile', 'identity', pid];

export const parseProfileIdentity = async (identity: Awaited<ReturnType<typeof getIdentity>>) => {
  let parsedIdentity: Identity | IdentityWithFallbacks | undefined;
  let isComplete = false;

  try {
    parsedIdentity = await identitySchema.parseAsync(identity);
    isComplete = true;
  } catch {
    parsedIdentity = await identityWithFallbacksSchema.parseAsync(identity ?? {});
  }

  return {
    identity: parsedIdentity,
    isComplete,
  };
};

const profileIdentityQueryFn = async () => parseProfileIdentity(await getIdentity());

const profileIdentityQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    queryFn: profileIdentityQueryFn,
    queryKey: profileIdentityQueryKey(pid),
  });

const useProfileIdentity = (enabled = true) => {
  const { pid } = useUserPid();
  const { enabled: queryOptionsEnabled, ...queryOptions } = profileIdentityQueryOptions(pid);
  const { data, ...rest } = useQuery({ ...queryOptions, enabled: queryOptionsEnabled && enabled });

  return {
    ...rest,
    data: data?.identity,
    isComplete: data?.isComplete,
  };
};

const useProfileIdentityMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postIdentity,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: profileIdentityQueryKey(pid) });
      queryClient.invalidateQueries({ queryKey: userAttributesQueryKey(pid) });
    },
  });
};

export { useProfileIdentity, useProfileIdentityMutation };
